import type { Attachment } from "@sd/types/ticket";

/** Match file UUIDs stored in Formily `attachments` (string or JSON array string). */
const FILE_UUID_RE =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

const UUID_IN_TEXT_RE =
  /[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/i;

export function isFileUuidString(v: string): boolean {
  return FILE_UUID_RE.test(v.trim());
}

export function mimeFromAttachmentFilename(name: string): string {
  const ext = name.split(".").pop()?.toLowerCase() ?? "";
  switch (ext) {
    case "png":
      return "image/png";
    case "jpg":
    case "jpeg":
      return "image/jpeg";
    case "gif":
      return "image/gif";
    case "webp":
      return "image/webp";
    case "webm":
      return "video/webm";
    case "mp4":
      return "video/mp4";
    case "ogg":
      return "video/ogg";
    case "mov":
      return "video/quicktime";
    case "avi":
      return "video/x-msvideo";
    case "pdf":
      return "application/pdf";
    case "doc":
      return "application/msword";
    case "docx":
      return "application/vnd.openxmlformats-officedocument.wordprocessingml.document";
    case "zip":
      return "application/zip";
    default:
      return "application/octet-stream";
  }
}

function uuidFromString(value: string): string | null {
  const trimmed = value.trim();
  if (!trimmed) return null;
  if (isFileUuidString(trimmed)) return trimmed;
  const match = trimmed.match(UUID_IN_TEXT_RE);
  return match ? match[0] : null;
}

function attachmentFromRecord(obj: Record<string, unknown>): Attachment | null {
  const idRaw =
    obj.id ?? obj.file_id ?? obj.file_uuid ?? obj.uuid ?? obj.attachment_id;
  let id = idRaw != null ? String(idRaw).trim() : "";
  if (!isFileUuidString(id)) {
    const fromPath = uuidFromString(
      String(obj.file_path ?? obj.url ?? obj.path ?? "")
    );
    if (fromPath) id = fromPath;
  }
  if (!isFileUuidString(id)) return null;

  const nameRaw =
    obj.file_original_name ??
    obj.original_name ??
    obj.filename ??
    obj.file_name ??
    obj.name ??
    "";
  const filePath = String(obj.file_path ?? obj.url ?? obj.path ?? "").trim();
  const name = String(
    nameRaw || filePath.split("/").pop() || `attachment-${id.slice(0, 8)}`
  );
  const mediaUrl = filePath || undefined;

  return {
    id,
    name,
    size: Number(obj.file_size ?? obj.size ?? 0) || 0,
    type: String(
      obj.file_type ?? obj.mime_type ?? mimeFromAttachmentFilename(name)
    ),
    url: `/file-utility/files/${id}/download/`,
    ...(mediaUrl ? { mediaUrl } : {}),
    uploadedBy: String(obj.uploaded_by ?? obj.created_by ?? "—"),
    uploadedAt: String(obj.created_at ?? obj.uploaded_at ?? ""),
    visibility: "public",
  };
}

function collectAttachmentIds(raw: unknown, ids: Set<string>): void {
  if (raw === null || raw === undefined) return;

  if (Array.isArray(raw)) {
    for (const item of raw) collectAttachmentIds(item, ids);
    return;
  }

  if (typeof raw === "object") {
    const obj = raw as Record<string, unknown>;
    const attachment = attachmentFromRecord(obj);
    if (attachment) {
      ids.add(attachment.id);
      return;
    }
    for (const key of ["id", "file_id", "file_uuid", "file_path", "url", "path"]) {
      if (key in obj) collectAttachmentIds(obj[key], ids);
    }
    return;
  }

  if (typeof raw === "string") {
    for (const id of parseAttachmentIdsFromApi(raw)) {
      ids.add(id);
    }
    const fromText = uuidFromString(raw);
    if (fromText) ids.add(fromText);
  }
}

function collectAttachmentPreviews(
  raw: unknown,
  previews: Map<string, Attachment>
): void {
  if (raw === null || raw === undefined) return;

  if (Array.isArray(raw)) {
    for (const item of raw) collectAttachmentPreviews(item, previews);
    return;
  }

  if (typeof raw === "object") {
    const attachment = attachmentFromRecord(raw as Record<string, unknown>);
    if (attachment) previews.set(attachment.id, attachment);
  }
}

/**
 * Parse `attachments` from API: single UUID, `"[\"uuid\"]"`, `[]`, or array of ids/objects.
 */
export function parseAttachmentIdsFromApi(raw: unknown): string[] {
  if (raw === null || raw === undefined) {
    return [];
  }
  if (Array.isArray(raw)) {
    const out: string[] = [];
    for (const item of raw) {
      if (typeof item === "string" && isFileUuidString(item)) {
        out.push(item.trim());
      } else if (item && typeof item === "object" && "id" in item) {
        const id = String((item as { id: unknown }).id ?? "").trim();
        if (isFileUuidString(id)) {
          out.push(id);
        }
      } else if (item && typeof item === "object") {
        const attachment = attachmentFromRecord(item as Record<string, unknown>);
        if (attachment) out.push(attachment.id);
      } else if (typeof item === "string") {
        const id = uuidFromString(item);
        if (id) out.push(id);
      }
    }
    return out;
  }
  const s = String(raw).trim();
  if (!s || s === "[]" || s === "null") {
    return [];
  }
  if (isFileUuidString(s)) {
    return [s.trim()];
  }
  try {
    const parsed = JSON.parse(s) as unknown;
    if (Array.isArray(parsed)) {
      return parsed
        .map(x => {
          if (typeof x === "string") {
            return uuidFromString(x) ?? (isFileUuidString(x) ? x.trim() : "");
          }
          if (x && typeof x === "object") {
            return attachmentFromRecord(x as Record<string, unknown>)?.id ?? "";
          }
          return "";
        })
        .filter(x => isFileUuidString(x));
    }
    if (typeof parsed === "string") {
      const id = uuidFromString(parsed);
      if (id) return [id];
    }
  } catch {
    /* not JSON */
  }
  const fromText = uuidFromString(s);
  return fromText ? [fromText] : [];
}

function isExplicitEmptyAttachments(raw: unknown): boolean {
  if (Array.isArray(raw) && raw.length === 0) {
    return true;
  }
  if (typeof raw === "string") {
    const s = raw.trim();
    return s === "[]" || s === "null";
  }
  return false;
}

/** Parse attachment UUIDs + inline preview rows from ticket_create GET payload. */
export function parseTicketAttachmentsFromRecord(
  record: Record<string, unknown>
): { ids: string[]; previews: Attachment[] } {
  const ids = new Set<string>();
  const previews = new Map<string, Attachment>();

  if ("attachments" in record && isExplicitEmptyAttachments(record.attachments)) {
    return { ids: [], previews: [] };
  }

  for (const key of [
    "attachments",
    "attachment",
    "attachment_files",
    "file_path",
    "file_paths",
  ]) {
    if (!(key in record)) continue;
    const raw = record[key];
    collectAttachmentIds(raw, ids);
    collectAttachmentPreviews(raw, previews);
  }

  return {
    ids: [...ids],
    previews: [...previews.values()],
  };
}

/** Build attachment rows from ticket_create GET — no per-file API calls. */
export function buildAttachmentsFromTicketRecord(
  record: Record<string, unknown>
): Attachment[] {
  const { ids, previews } = parseTicketAttachmentsFromRecord(record);
  const previewById = new Map(previews.map(p => [p.id, p]));
  return ids.map(id => {
    const existing = previewById.get(id);
    if (existing) return existing;
    return {
      id,
      name: `attachment-${id.slice(0, 8)}`,
      size: 0,
      type: "application/octet-stream",
      url: `/file-utility/files/${id}/download/`,
      uploadedBy: "—",
      uploadedAt: "",
      visibility: "public",
    };
  });
}
